
import Link from "@/components/ui/link-compat";
import { useRouter, useSearchParams } from "@/lib/router-compat";
import { useAuth } from "@/contexts/AuthContext";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useSuperAdminOrgDetail } from "@/hooks/useSuperAdmin";
import { formatDate } from "@/lib/utils";
import { Users, Contact, Briefcase, FolderKanban, CheckSquare } from "lucide-react";
import { DetailPageSkeleton } from "@/components/ui/skeletons";
import { PageHeader } from "@/components/ui/page-header";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function OrgDetailPage() {
  const searchParams = useSearchParams();
  const id = searchParams.get("id") ?? "";
  const { isSuperAdmin } = useAuth();
  const router = useRouter();
  const { data: org, isLoading } = useSuperAdminOrgDetail(id);
  usePageTitle(org?.name ?? 'Organization');

  if (!isSuperAdmin) {
    router.push("/dashboard");
    return null;
  }

  if (isLoading) {
    return <DetailPageSkeleton />;
  }

  if (!org) {
    return <div className="flex items-center justify-center py-10">Organization not found.</div>;
  }

  const counts = org.entityCounts;

  return (
    <div className="space-y-6">
      <PageHeader
        title={org.name}
        description={[org.description, `Created ${formatDate(org.createdAt)}`]
          .filter(Boolean)
          .join(" · ")}
        breadcrumb={[
          { label: "Admin", href: "/dashboard/settings/admin" },
          { label: org.name },
        ]}
        askAi
        hideTitleOnMobile={false}
      />

      {/* Entity counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Contact className="h-4 w-4" /> Contacts</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{counts.contacts}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Briefcase className="h-4 w-4" /> Deals</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{counts.deals}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><FolderKanban className="h-4 w-4" /> Projects</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{counts.projects}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><CheckSquare className="h-4 w-4" /> Tasks</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{counts.tasks}</p></CardContent>
        </Card>
      </div>

      {/* Members table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Members ({org.members.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {org.members.map((member) => (
                <TableRow key={member.userId}>
                  <TableCell>
                    <Link href={`/dashboard/settings/admin/user?id=${member.userId}`} className="text-primary hover:underline">
                      {member.email}
                    </Link>
                  </TableCell>
                  <TableCell>{[member.firstName, member.lastName].filter(Boolean).join(" ") || "-"}</TableCell>
                  <TableCell><Badge variant={member.role === "Owner" ? "default" : "secondary"}>{member.role}</Badge></TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDate(member.joinedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/settings/admin/organization')({ component: OrgDetailPage });
